import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, limit, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import type { Summary, Target } from '../types';

/**
 * This member's client scope, read from their own membership document.
 *
 * The rules enforce the scope; this exists so the QUERY matches what the rules
 * will allow. Firestore rejects an entire query if any matched document fails
 * a rule, so a scoped listener that asked for the whole collection would not
 * return a filtered list - it would return a permission error and an empty
 * board.
 */
export type ScopeMode = 'all' | 'include' | 'exclude';

export function useClientScope(orgId: string | null): {
  mode: ScopeMode; viewAll: boolean; clientIds: string[]; ready: boolean;
} {
  const [scope, setScope] = useState<{
    mode: ScopeMode; viewAll: boolean; clientIds: string[]; ready: boolean;
  }>({ mode: 'all', viewAll: true, clientIds: [], ready: false });

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!orgId || !uid) {
      setScope({ mode: 'all', viewAll: true, clientIds: [], ready: Boolean(!orgId) });
      return;
    }

    return onSnapshot(
      doc(db, 'orgs', orgId, 'members', uid),
      (snap) => {
        const d = snap.data() ?? {};
        // Absent means unrestricted - the same default the rules use, and it
        // has to be the same or the UI and the rules disagree about what you
        // are allowed to see.
        const mode = (d.scopeMode ?? (d.viewAllClients === false ? 'include' : 'all')) as ScopeMode;
        setScope({
          mode,
          viewAll: mode === 'all',
          clientIds: Array.isArray(d.clientIds) ? (d.clientIds as string[]) : [],
          ready: true,
        });
      },
      // Cannot read your own membership: assume unrestricted and let the rules
      // decide. Guessing "restricted" here would blank the board for everyone
      // on a transient error.
      () => setScope({ mode: 'all', viewAll: true, clientIds: [], ready: true }),
    );
  }, [orgId]);

  return scope;
}

/** Firestore caps an `in` filter at 30 values. */
const IN_LIMIT = 30;

function chunk<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

/**
 * Live subscriptions to one organisation's data.
 *
 * Every path is built from `orgs/{orgId}/…`, so a hook can only ever see the
 * tenant it was given. There is no query here that could accidentally span
 * organisations — the org id is a required argument rather than an optional
 * filter, which is the whole point of nesting the data.
 *
 * These are onSnapshot listeners rather than polling: the collectors write
 * every 15 minutes, and when they do, every open tab updates immediately.
 */
export function useTargets(orgId: string | null): {
  targets: Target[]; loading: boolean; error: string | null;
} {
  const [targets, setTargets] = useState<Target[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const scope = useClientScope(orgId);

  useEffect(() => {
    if (!orgId) { setTargets([]); setLoading(false); return; }
    // Wait for the scope: subscribing unfiltered first would flash the whole
    // estate at a restricted person before the correct query replaced it.
    if (!scope.ready) { setLoading(true); return; }
    setLoading(true);

    const col = collection(db, 'orgs', orgId, 'targets');

    if (scope.viewAll) {
      return onSnapshot(
        col,
        (snap) => {
          setTargets(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Target));
          setLoading(false);
          setError(null);
        },
        (err) => { setError(err.message); setLoading(false); },
      );
    }

    /**
     * EXCLUDE MODE: everything except a short list.
     *
     * Two listeners, because Firestore's `not-in` skips documents where the
     * field is null - so a machine not yet matched to a client would vanish,
     * which is precisely the machine this person most needs to see. The second
     * listener puts them back.
     */
    if (scope.mode === 'exclude' && scope.clientIds.length > 0) {
      const parts = new Map<string, Target[]>();
      const push = (key: string, docs: Target[]) => {
        parts.set(key, docs);
        const byId = new Map<string, Target>();
        for (const list of parts.values()) for (const t of list) byId.set(t.id, t);
        setTargets([...byId.values()]);
        setLoading(false);
        setError(null);
      };

      const unsubA = onSnapshot(
        query(col, where('clientId', 'not-in', scope.clientIds.slice(0, 10))),
        (snap) => push('rest', snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Target)),
        (err) => { setError(err.message); setLoading(false); },
      );
      const unsubB = onSnapshot(
        query(col, where('clientId', '==', null)),
        (snap) => push('unmapped', snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Target)),
        // Unmapped machines are a bonus here, not the point - a failure to read
        // them must not blank the rest.
        () => push('unmapped', []),
      );
      return () => { unsubA(); unsubB(); };
    }

    if (scope.clientIds.length === 0) {
      // Restricted to nothing. setMemberClients refuses to save this, so it
      // means the data was edited elsewhere - show an empty board and say so
      // rather than silently showing everything.
      setTargets([]);
      setLoading(false);
      setError('No clients are assigned to you yet. Ask an owner or admin.');
      return;
    }

    /**
     * ONE LISTENER PER 30 CLIENTS, merged.
     *
     * Firestore's `in` takes at most 30 values, and an MSP can easily assign
     * somebody more than that. Chunking keeps it live rather than falling back
     * to polling, at the cost of holding each chunk's results and recombining
     * them whenever any chunk changes.
     */
    const chunks = chunk(scope.clientIds, IN_LIMIT);
    const parts = new Map<number, Target[]>();
    let settled = 0;

    const unsubs = chunks.map((ids, i) =>
      onSnapshot(
        query(col, where('clientId', 'in', ids)),
        (snap) => {
          parts.set(i, snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Target));
          // De-duplicated on id: a machine can only be in one chunk today, but
          // merging by key means that stays true if that ever changes.
          const byId = new Map<string, Target>();
          for (const list of parts.values()) for (const t of list) byId.set(t.id, t);
          setTargets([...byId.values()]);
          if (++settled >= chunks.length) setLoading(false);
          setError(null);
        },
        (err) => { setError(err.message); setLoading(false); },
      ),
    );

    return () => unsubs.forEach((u) => u());
  }, [orgId, scope.ready, scope.mode, scope.viewAll, scope.clientIds.join(',')]);

  return { targets, loading, error };
}

export function useSummary(orgId: string | null): Summary | null {
  const [summary, setSummary] = useState<Summary | null>(null);

  useEffect(() => {
    if (!orgId) { setSummary(null); return; }
    const unsub = onSnapshot(
      doc(db, 'orgs', orgId, 'meta', 'summary'),
      (snap) => setSummary(snap.exists() ? (snap.data() as Summary) : null),
      () => setSummary(null)
    );
    return unsub;
  }, [orgId]);

  return summary;
}

export interface StatusChange {
  id: string;
  targetId: string;
  source: string;
  name: string;
  client: string;
  from: string;
  to: string;
  reason: string;
  at: { toDate(): Date } | null;
}

export function useStatusChanges(orgId: string | null, max = 200): StatusChange[] {
  const [changes, setChanges] = useState<StatusChange[]>([]);

  useEffect(() => {
    if (!orgId) { setChanges([]); return; }
    const q = query(
      collection(db, 'orgs', orgId, 'statusChanges'),
      orderBy('at', 'desc'),
      limit(max)
    );
    const unsub = onSnapshot(
      q,
      (snap) => setChanges(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as StatusChange)),
      () => setChanges([])
    );
    return unsub;
  }, [orgId, max]);

  return changes;
}
